import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { cohortsAPI, coursesAPI, currenciesAPI, enrollmentsAPI, gradesAPI, homeworkAPI, paymentsAPI, reportsAPI, studiesAPI, usersAPI } from '../services/api';
import { formatUserDisplayName } from '../utils/userDisplayName';

const DataContext = createContext(null);
const GRADES_FETCH_LIMIT = 100000;
const ENROLLMENTS_PAGE_LIMIT = 100000;

export const DataProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [homework, setHomework] = useState([]);
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [studies, setStudies] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const loadDataPromiseRef = useRef(null);

  const locations = useMemo(() => [
    { id: 'paramaribo', name: 'Paramaribo' },
    { id: 'nickerie', name: 'Nickerie' }
  ], []);

  const toLocationId = (locationValue) => {
    if (!locationValue) return '';
    return String(locationValue).trim().toLowerCase();
  };

  const toCanonicalLocation = (locationValue) => {
    const normalized = toLocationId(locationValue);
    if (!normalized) return undefined;
    if (normalized === 'paramaribo') return 'Paramaribo';
    if (normalized === 'nickerie') return 'Nickerie';
    return `${normalized}`.charAt(0).toUpperCase() + `${normalized}`.slice(1);
  };

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('nl-NL');
  };

  const normalizePaymentTypeKey = (value) => {
    const paymentTypeMap = {
      'school-year': 'schoolYear',
      'one-time': 'oneTime',
      'enrollment-fee': 'enrollmentFee'
    };
    return paymentTypeMap[value] || value || 'oneTime';
  };

  const normalizeGradeRecord = (g) => {
    const rawGrade = typeof g.grade === 'number' ? g.grade : null;
    const score = rawGrade !== null ? Math.round(rawGrade * 10) : null;
    return {
      ...g,
      studentId: g.student?.id || g.student?._id || g.studentId || g.student,
      courseId: g.course?.id || g.course?._id || g.courseId || g.course,
      examPointKey: g.examPointKey || '',
      score,
      status: rawGrade !== null ? (rawGrade >= 5.5 ? 'passed' : 'failed') : 'pending',
      date: g.gradeDate
    };
  };

  const getSchoolYearLabel = (payment) => {
    if (payment.schoolYear) {
      return payment.schoolYear;
    }

    const sourceDate = payment.dueDate || payment.paymentDate || payment.createdAt;
    if (!sourceDate) {
      return 'Onbekend studiejaar';
    }

    const date = new Date(sourceDate);
    if (Number.isNaN(date.getTime())) {
      return 'Onbekend studiejaar';
    }

    return `${date.getFullYear()}`;
  };

  const safeApiCall = async (promise, fallbackValue, label) => {
    try {
      return await promise;
    } catch (error) {
      console.error(`API call failed (${label})`, error);
      return fallbackValue;
    }
  };

  const fetchAllEnrollments = useCallback(async (params = {}, label = 'enrollments.getAll') => {
    const firstPage = await safeApiCall(
      enrollmentsAPI.getAll({ ...params, page: 1, limit: ENROLLMENTS_PAGE_LIMIT }),
      { enrollments: [], pagination: { page: 1, pages: 1 } },
      `${label}.page1`
    );

    const firstPageEnrollments = Array.isArray(firstPage?.enrollments) ? firstPage.enrollments : [];
    const totalPages = Number(firstPage?.pagination?.pages || 1);
    if (!Number.isFinite(totalPages) || totalPages <= 1) {
      return firstPageEnrollments;
    }

    const pageRequests = [];
    for (let page = 2; page <= totalPages; page += 1) {
      pageRequests.push(
        safeApiCall(
          enrollmentsAPI.getAll({ ...params, page, limit: ENROLLMENTS_PAGE_LIMIT }),
          { enrollments: [] },
          `${label}.page${page}`
        )
      );
    }

    const extraPages = await Promise.all(pageRequests);
    return [
      ...firstPageEnrollments,
      ...extraPages.flatMap((resp) => (Array.isArray(resp?.enrollments) ? resp.enrollments : []))
    ];
  }, []);

  const loadData = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setCourses([]);
      setEnrollments([]);
      setGrades([]);
      setHomework([]);
      setPayments([]);
      setStudents([]);
      setInstructors([]);
      setStudies([]);
      setCohorts([]);
      setCurrencies([]);
      setUsers([]);
      setSchedules([]);
      setDepartments([]);
      return;
    }

    if (loadDataPromiseRef.current) {
      return loadDataPromiseRef.current;
    }

    const loadPromise = (async () => {
    setLoading(true);
    try {
      const [coursesResp, studiesResp, schedulesResp, cohortsResp, currenciesResp] = await Promise.all([
        safeApiCall(coursesAPI.getAll({ limit: 100000 }), { courses: [] }, 'courses.getAll'),
        safeApiCall(studiesAPI.getAll({ limit: 100000, includeYears: 'true' }), { studies: [] }, 'studies.getAll'),
        safeApiCall(coursesAPI.getCalendarEvents(), [], 'courses.getCalendarEvents'),
        (user.role === 'admin' || user.role === 'manager')
          ? safeApiCall(cohortsAPI.getAll({ limit: 100000 }), { cohorts: [] }, 'cohorts.getAll')
          : Promise.resolve({ cohorts: [] }),
        safeApiCall(currenciesAPI.getAll(), { currencies: [] }, 'currencies.getAll')
      ]);

      const normalizedCourses = (coursesResp.courses || []).map(c => ({
        ...c,
        id: c.id || c._id
      }));

      const hydratedStudies = (studiesResp.studies || []).map((study) => {
        const studyId = study.id || study._id;
        const studyYears = (study.studyYears || []).map((year) => ({
          id: year.id || year._id,
          year: year.yearNumber || year.year,
          yearNumber: year.yearNumber || year.year,
          name: year.name,
          startDate: year.startDate || '',
          endDate: year.endDate || '',
          modules: (year.modules || []).map((module) => {
            const moduleId = String(module.id || module._id);
            const moduleCourses = normalizedCourses
              .filter((course) => {
                const singleModuleId = String(course.module?.id || course.module?._id || course.module || '');
                const moduleIds = (course.modules || []).map((m) => String(m.id || m._id || m));
                return singleModuleId === moduleId || moduleIds.includes(moduleId);
              })
              .map((course) => ({
                ...course,
                id: course.id || course._id
              }));

            return {
              ...module,
              id: module.id || module._id,
              courses: moduleCourses,
              exams: module.exams || [],
              homework: module.homework || []
            };
          })
        }));

        return {
          ...study,
          id: studyId,
          cohorts: (study.cohorts || []).map((cohort) => ({
            ...cohort,
            id: cohort.id || cohort._id,
            location: toLocationId(cohort.location)
          })),
          studyYears
        };
      });

      setCourses(normalizedCourses);
      setStudies(hydratedStudies);
      setSchedules(schedulesResp || []);
      setCohorts((cohortsResp?.cohorts || []).map((cohort) => ({
        ...cohort,
        location: toLocationId(cohort.location)
      })));
      setCurrencies(currenciesResp?.currencies || []);

      if (user.role === 'admin' || user.role === 'manager') {
        const usersResp = await safeApiCall(usersAPI.getAll({ limit: 1000 }), { users: [] }, 'users.getAll');
        const normalizedUsers = (usersResp.users || []).map(u => ({
          ...u,
          name: formatUserDisplayName(u),
          location: toLocationId(u.location),
          cohort: u.cohort ? { ...u.cohort, location: toLocationId(u.cohort.location) } : null
        }));
        setUsers(normalizedUsers);
      } else {
        setUsers([]);
      }

      if (user.role === 'student') {
        const userId = user.id || user._id;
        const [allEnrollments, gradesResp, homeworkResp, paymentsResp] = await Promise.all([
          fetchAllEnrollments({ studentId: userId }, 'enrollments.getAll.student'),
          safeApiCall(gradesAPI.getAll({ studentId: userId, limit: GRADES_FETCH_LIMIT }), { grades: [] }, 'grades.getAll.student'),
          safeApiCall(homeworkAPI.getAll({ limit: 1000 }), { homework: [] }, 'homework.getAll.student'),
          safeApiCall(paymentsAPI.getAll({ studentId: userId, limit: 1000 }), { payments: [] }, 'payments.getAll.student')
        ]);

        const normalizedEnrollments = (allEnrollments || []).map(e => ({
          ...e,
          studentId: e.student?.id || e.student?._id || e.studentId || e.student,
          courseId: e.course?.id || e.course?._id || e.courseId || e.course,
          enrollmentDate: e.enrollmentDate || e.createdAt || null,
          enrolledOn: formatDate(e.enrollmentDate || e.createdAt)
        }));

        const normalizedGrades = (gradesResp.grades || []).map(g => {
          const rawGrade = typeof g.grade === 'number' ? g.grade : null;
          const score = rawGrade !== null ? Math.round(rawGrade * 10) : null;
          return {
            ...g,
            studentId: g.student?.id || g.student?._id || g.studentId || g.student,
            courseId: g.course?.id || g.course?._id || g.courseId || g.course,
            examPointKey: g.examPointKey || '',
            score,
            status: rawGrade !== null ? (rawGrade >= 5.5 ? 'passed' : 'failed') : 'pending',
            date: g.gradeDate
          };
        });

        const normalizedHomework = (homeworkResp.homework || []).map(h => ({
          ...h,
          courseId: h.course?.id || h.course
        }));

        const normalizedPayments = (paymentsResp.payments || []).map(p => ({
          ...p,
          studentId: p.student?.id || p.student,
          courseId: p.course?.id || p.course,
          totalAmount: Number((p.totalAmount ?? p.amount) || 0),
          paidAmount: Number(p.paidAmount || 0),
          remainingAmount: Math.max(0, Number((p.totalAmount ?? p.amount) || 0) - Number(p.paidAmount || 0)),
          paymentTypeKey: normalizePaymentTypeKey(p.paymentType),
          schoolYearLabel: getSchoolYearLabel(p),
          paymentDateLabel: formatDate(p.paymentDate),
          dueDateLabel: formatDate(p.dueDate)
        }));

        setEnrollments(normalizedEnrollments);
        setGrades(normalizedGrades);
        setHomework(normalizedHomework);
        setPayments(normalizedPayments);
      } else {
        const [allEnrollments, gradesResp, homeworkResp, paymentsResp, studentsResp, instructorsResp] = await Promise.all([
          fetchAllEnrollments({}, 'enrollments.getAll.admin'),
          safeApiCall(gradesAPI.getAll({ limit: GRADES_FETCH_LIMIT }), { grades: [] }, 'grades.getAll.admin'),
          safeApiCall(homeworkAPI.getAll({ limit: 1000 }), { homework: [] }, 'homework.getAll.admin'),
          safeApiCall(paymentsAPI.getAll({ limit: 1000 }), { payments: [] }, 'payments.getAll.admin'),
          safeApiCall(usersAPI.getStudents(), [], 'users.getStudents'),
          safeApiCall(usersAPI.getInstructors(), [], 'users.getInstructors')
        ]);

        const normalizedEnrollments = (allEnrollments || []).map(e => ({
          ...e,
          studentId: e.student?.id || e.student?._id || e.studentId || e.student,
          courseId: e.course?.id || e.course?._id || e.courseId || e.course,
          enrollmentDate: e.enrollmentDate || e.createdAt || null,
          enrolledOn: formatDate(e.enrollmentDate || e.createdAt)
        }));

        const normalizedGrades = (gradesResp.grades || []).map(g => {
          const rawGrade = typeof g.grade === 'number' ? g.grade : null;
          const score = rawGrade !== null ? Math.round(rawGrade * 10) : null;
          return {
            ...g,
            studentId: g.student?.id || g.student?._id || g.studentId || g.student,
            courseId: g.course?.id || g.course?._id || g.courseId || g.course,
            examPointKey: g.examPointKey || '',
            score,
            status: rawGrade !== null ? (rawGrade >= 5.5 ? 'passed' : 'failed') : 'pending',
            date: g.gradeDate
          };
        });

        const normalizedHomework = (homeworkResp.homework || []).map(h => ({
          ...h,
          courseId: h.course?.id || h.course
        }));

        const normalizedPayments = (paymentsResp.payments || []).map(p => ({
          ...p,
          studentId: p.student?.id || p.student,
          courseId: p.course?.id || p.course,
          totalAmount: Number((p.totalAmount ?? p.amount) || 0),
          paidAmount: Number(p.paidAmount || 0),
          remainingAmount: Math.max(0, Number((p.totalAmount ?? p.amount) || 0) - Number(p.paidAmount || 0)),
          paymentTypeKey: normalizePaymentTypeKey(p.paymentType),
          schoolYearLabel: getSchoolYearLabel(p),
          paymentDateLabel: formatDate(p.paymentDate),
          dueDateLabel: formatDate(p.dueDate)
        }));

        setEnrollments(normalizedEnrollments);
        setGrades(normalizedGrades);
        setHomework(normalizedHomework);
        setPayments(normalizedPayments);
        setStudents((studentsResp || []).map(s => ({
          ...s,
          name: formatUserDisplayName(s),
          location: toLocationId(s.location),
          cohort: s.cohort ? { ...s.cohort, location: toLocationId(s.cohort.location) } : null
        })));
        setInstructors((instructorsResp || []).map(i => ({
          ...i,
          name: formatUserDisplayName(i),
          location: toLocationId(i.location)
        })));
      }
    } catch (error) {
      console.error('Failed to load data', error);
    } finally {
      setLoading(false);
      loadDataPromiseRef.current = null;
    }
    })();

    loadDataPromiseRef.current = loadPromise;
    return loadPromise;
  }, [fetchAllEnrollments, user, isAuthenticated]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refresh = async () => {
    await loadData();
  };

  // Helper selectors
  const getCourseById = (courseId) => courses.find(c => String(c.id || c._id) === String(courseId));
  const getStudentEnrollments = (studentId) => {
    return enrollments.filter(e => String(e.studentId || e.student?.id || e.student) === String(studentId));
  };

  const getStudentGrades = (studentId) => {
    return grades.filter(g => String(g.studentId || g.student?.id || g.student) === String(studentId));
  };

  const getStudentHomeworkSubmissions = (studentId) => {
    const targetStudentId = String(studentId);

    // Some API payloads include submissions under each homework item; older payloads do not.
    return (homework || []).flatMap((item) => {
      const submissions = Array.isArray(item?.submissions) ? item.submissions : [];
      return submissions
        .filter((submission) => String(submission.studentId || submission.student?.id || submission.student) === targetStudentId)
        .map((submission) => ({
          ...submission,
          homeworkId: submission.homeworkId || item.id || item._id
        }));
    });
  };

  const getStudentPayments = (studentId) => {
    return payments.filter(p => String(p.studentId || p.student?.id || p.student) === String(studentId));
  };

  const calculatePaymentSummary = (studentId) => {
    const studentPayments = getStudentPayments(studentId);
    return studentPayments.reduce((acc, payment) => {
      const totalAmount = Number((payment.totalAmount ?? payment.amount) || 0);
      const paidAmount = Number(payment.paidAmount || 0);
      const remaining = Math.max(0, totalAmount - paidAmount);

      acc.totalDue += totalAmount;
      acc.totalPaid += paidAmount;
      acc.pending += remaining;

      if (remaining <= 0) {
        acc.paidCount += 1;
      } else {
        acc.pendingCount += 1;
      }

      if (payment.status === 'overdue') {
        acc.overdue += remaining;
      }

      return acc;
    }, { totalDue: 0, totalPaid: 0, pending: 0, overdue: 0, count: studentPayments.length, paidCount: 0, pendingCount: 0 });
  };

  const calculateECTS = (studentId) => {
    const studentGrades = getStudentGrades(studentId);
    const passedCourseIds = new Set(
      studentGrades
        .filter(g => typeof g.grade === 'number' && g.grade >= 5.5)
        .map(g => String(g.courseId || g.course?.id || g.course))
    );

    return courses
      .filter(c => passedCourseIds.has(String(c.id || c._id)))
      .reduce((sum, course) => sum + Number(course.ects || 0), 0);
  };

  const getCourseSchedule = (courseId) => {
    const course = getCourseById(courseId);
    return course?.schedule || [];
  };

  const getUsersByRole = (role) => {
    return users.filter(u => u.role === role);
  };

  const enrollStudent = async (studentId, courseId, notificationSettings = {}) => {
    try {
      const payload = { 
        studentId, 
        courseId,
        sendNotification: notificationSettings.sendEmail || notificationSettings.sendSms,
        notificationChannels: {
          email: notificationSettings.sendEmail || false,
          sms: notificationSettings.sendSms || false
        }
      };
      await enrollmentsAPI.create(payload);
      await loadData();
    } catch (error) {
      console.error('Enroll student failed', error);
      throw error;
    }
  };

  const bulkEnrollStudents = async (studentIds, courseId, notificationSettings = {}) => {
    try {
      const payload = { 
        studentIds, 
        courseId,
        sendNotification: notificationSettings.sendEmail || notificationSettings.sendSms,
        notificationChannels: {
          email: notificationSettings.sendEmail || false,
          sms: notificationSettings.sendSms || false
        }
      };
      const result = await enrollmentsAPI.bulkEnroll(payload);
      await loadData();
      return result;
    } catch (error) {
      console.error('Bulk enroll students failed', error);
      throw error;
    }
  };

  const bulkDeleteEnrollments = async (enrollmentIds) => {
    try {
      const result = await enrollmentsAPI.bulkDelete({ enrollmentIds });
      await loadData();
      return result;
    } catch (error) {
      console.error('Bulk delete enrollments failed', error);
      throw error;
    }
  };

  const unenrollStudent = async (enrollmentId) => {
    try {
      await enrollmentsAPI.delete(enrollmentId);
      await loadData();
    } catch (error) {
      console.error('Unenroll student failed', error);
    }
  };

  const updateEnrollment = async (enrollmentId, updates) => {
    try {
      await enrollmentsAPI.update(enrollmentId, updates);
      await loadData();
    } catch (error) {
      console.error('Update enrollment failed', error);
      throw error;
    }
  };

  const addUser = async (userData) => {
    try {
      const nameParts = (userData.name || '').trim().split(/\s+/).filter(Boolean);
      const firstName = userData.firstName || nameParts[0] || '';
      const lastName = userData.lastName || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '.');
      const payload = {
        email: userData.email,
        role: userData.role,
        firstName,
        lastName,
        location: toCanonicalLocation(userData.location),
        phone: userData.phone,
        profilePhotoUrl: userData.profilePhotoUrl,
        address: userData.address,
        dateOfBirth: userData.dateOfBirth,
        password: userData.password || 'password123',
        ...(userData.accessRoleId !== undefined ? { accessRoleId: userData.accessRoleId } : {})
      };
      await usersAPI.create(payload);
      await loadData();
    } catch (error) {
      console.error('Add user failed', error);
      throw error;
    }
  };

  const enrollStudentUser = async (studentData) => {
    try {
      const payload = {
        ...studentData,
        location: toCanonicalLocation(studentData.location)
      };
      await usersAPI.enrollStudent(payload);
      await loadData();
    } catch (error) {
      console.error('Enroll student user failed', error);
      throw error;
    }
  };

  const deleteUser = async (userId) => {
    try {
      await usersAPI.delete(userId);
      await loadData();
    } catch (error) {
      console.error('Delete user failed', error);
      throw error;
    }
  };

  const addCohort = async (cohortData) => {
    try {
      const payload = {
        ...cohortData,
        location: toCanonicalLocation(cohortData.location)
      };
      await cohortsAPI.create(payload);
      await loadData();
    } catch (error) {
      console.error('Add cohort failed', error);
      throw error;
    }
  };

  const updateCohort = async (cohortId, cohortData) => {
    try {
      const payload = {
        ...cohortData,
        location: toCanonicalLocation(cohortData.location)
      };
      await cohortsAPI.update(cohortId, payload);
      await loadData();
    } catch (error) {
      console.error('Update cohort failed', error);
      throw error;
    }
  };

  const deleteCohort = async (cohortId) => {
    try {
      await cohortsAPI.delete(cohortId);
      await loadData();
    } catch (error) {
      console.error('Delete cohort failed', error);
      throw error;
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      const payload = {
        ...userData,
        location: toCanonicalLocation(userData.location)
      };
      await usersAPI.update(userId, payload);
      await loadData();
    } catch (error) {
      console.error('Update user failed', error);
      throw error;
    }
  };

  const addStudy = async (studyData) => {
    try {
      await studiesAPI.create(studyData);
      await loadData();
    } catch (error) {
      console.error('Add study failed', error);
      throw error;
    }
  };

  const updateStudy = async (studyId, studyData) => {
    try {
      await studiesAPI.update(studyId, studyData);
      await loadData();
    } catch (error) {
      console.error('Update study failed', error);
      throw error;
    }
  };

  const deleteStudy = async (studyId) => {
    try {
      await studiesAPI.delete(studyId);
      await loadData();
    } catch (error) {
      console.error('Delete study failed', error);
      throw error;
    }
  };

  const addStudyYear = async (studyId, yearData) => {
    try {
      const yearNumber = Number(yearData.yearNumber || yearData.year);
      await studiesAPI.createYear(studyId, {
        yearNumber,
        name: yearData.name || `Study Year ${yearNumber}`
      });
      await loadData();
    } catch (error) {
      console.error('Add study year failed', error);
      throw error;
    }
  };

  const updateStudyYear = async (studyId, yearId, yearData) => {
    try {
      await studiesAPI.updateYear(studyId, yearId, yearData);
      await loadData();
    } catch (error) {
      console.error('Update study year failed', error);
      throw error;
    }
  };

  const deleteStudyYear = async (studyId, yearId) => {
    try {
      await studiesAPI.deleteYear(studyId, yearId);
      await loadData();
    } catch (error) {
      console.error('Delete study year failed', error);
      throw error;
    }
  };

  const addModule = async (studyId, yearId, moduleData) => {
    try {
      await studiesAPI.createModule(studyId, yearId, moduleData);
      await loadData();
    } catch (error) {
      console.error('Add module failed', error);
      throw error;
    }
  };

  const addCourse = async (courseData) => {
    try {
      await coursesAPI.create(courseData);
      await loadData();
    } catch (error) {
      console.error('Add course failed', error);
      throw error;
    }
  };

  const addCourseModule = async (courseId, moduleData) => {
    try {
      await coursesAPI.addModuleToCourse(courseId, moduleData);
      await loadData();
    } catch (error) {
      console.error('Add course module failed', error);
      throw error;
    }
  };

  const updateCourseModule = async (studyId, yearId, moduleId, moduleData) => {
    try {
      await studiesAPI.updateModule(studyId, yearId, moduleId, moduleData);
      await loadData();
    } catch (error) {
      console.error('Update course module failed', error);
      throw error;
    }
  };

  const updateCourse = async (courseId, courseData) => {
    try {
      await coursesAPI.update(courseId, courseData);
      await loadData();
    } catch (error) {
      console.error('Update course failed', error);
      throw error;
    }
  };

  const deleteCourse = async (courseId) => {
    try {
      await coursesAPI.delete(courseId);
      await loadData();
    } catch (error) {
      console.error('Delete course failed', error);
      throw error;
    }
  };

  const addCourseExamPoint = async (courseId, examPointData) => {
    try {
      await coursesAPI.addExamPointToCourse(courseId, examPointData);
      await loadData();
    } catch (error) {
      console.error('Add course exam point failed', error);
      throw error;
    }
  };

  const deleteCourseModule = async (courseId, moduleId) => {
    try {
      await coursesAPI.deleteModuleFromCourse(courseId, moduleId);
      await loadData();
    } catch (error) {
      console.error('Delete course module failed', error);
      throw error;
    }
  };

  const deleteCourseExamPoint = async (courseId, index) => {
    try {
      await coursesAPI.deleteExamPointFromCourse(courseId, index);
      await loadData();
    } catch (error) {
      console.error('Delete course exam point failed', error);
      throw error;
    }
  };

  const updateCourseExamPoint = async (courseId, index, pointData) => {
    try {
      await coursesAPI.updateExamPointInCourse(courseId, index, pointData);
      await loadData();
    } catch (error) {
      console.error('Update course exam point failed', error);
      throw error;
    }
  };

  const addPayment = async (paymentData) => {
    try {
      await paymentsAPI.create(paymentData);
      await loadData();
    } catch (error) {
      console.error('Add payment failed', error);
      throw error;
    }
  };

  const registerPayment = async (paymentId, amount, paymentMethod, notes, paymentDate) => {
    try {
      await paymentsAPI.update(paymentId, {
        paymentAmount: amount,
        paymentDate: paymentDate || new Date().toISOString(),
        paymentMethod,
        paymentNotes: notes,
        notes: notes || `Registered payment amount ${amount}`
      });
      await loadData();
    } catch (error) {
      console.error('Register payment failed', error);
      throw error;
    }
  };

  const updatePayment = async (paymentId, paymentData) => {
    try {
      await paymentsAPI.update(paymentId, paymentData);
      await loadData();
    } catch (error) {
      console.error('Update payment failed', error);
      throw error;
    }
  };

  const deletePayment = async (paymentId) => {
    try {
      await paymentsAPI.delete(paymentId);
      await loadData();
    } catch (error) {
      console.error('Delete payment failed', error);
      throw error;
    }
  };

  const upsertGrade = async (gradeData) => {
    try {
      await gradesAPI.create(gradeData);
      const gradesResp = await gradesAPI.getAll({ limit: GRADES_FETCH_LIMIT });
      setGrades((gradesResp.grades || []).map(normalizeGradeRecord));
    } catch (error) {
      console.error('Upsert grade failed', error);
      throw error;
    }
  };

  const addDepartment = (department) => {
    setDepartments(prev => [...prev, { ...department, id: Date.now() }]);
  };

  const updateDepartment = (departmentId, updates) => {
    setDepartments(prev => prev.map(d => d.id === departmentId ? { ...d, ...updates } : d));
  };

  const deleteDepartment = (departmentId) => {
    setDepartments(prev => prev.filter(d => d.id !== departmentId));
  };

  const addCurrency = async (currencyData) => {
    try {
      await currenciesAPI.create(currencyData);
      await loadData();
    } catch (error) {
      console.error('Add currency failed', error);
      throw error;
    }
  };

  const updateCurrency = async (currencyId, currencyData) => {
    try {
      await currenciesAPI.update(currencyId, currencyData);
      await loadData();
    } catch (error) {
      console.error('Update currency failed', error);
      throw error;
    }
  };

  const deleteCurrency = async (currencyId) => {
    try {
      await currenciesAPI.delete(currencyId);
      await loadData();
    } catch (error) {
      console.error('Delete currency failed', error);
      throw error;
    }
  };

  const addSchedule = async (courseId, schedule) => {
    try {
      const startDateTime = schedule.startDateTime
        ? new Date(schedule.startDateTime)
        : new Date(`${schedule.date}T${schedule.startTime}`);
      const endDateTime = schedule.endDateTime
        ? new Date(schedule.endDateTime)
        : new Date(`${schedule.date}T${schedule.endTime}`);
      await coursesAPI.addSchedule(courseId, {
        title: schedule.title || 'College',
        description: schedule.description || '',
        startDateTime,
        endDateTime,
        location: schedule.location,
        eventType: schedule.eventType || 'college',
        examType: schedule.examType || undefined,
        cohortId: schedule.cohortId || undefined,
        studyId: schedule.studyId || undefined,
        mode: schedule.mode || 'onsite',
        meetingUrl: schedule.meetingUrl || undefined
      });
      const updatedSchedules = await coursesAPI.getCalendarEvents();
      setSchedules(updatedSchedules || []);
      await loadData();
    } catch (error) {
      console.error('Add schedule failed', error);
      throw error;
    }
  };

  const updateSchedule = async (courseId, scheduleId, schedule) => {
    try {
      const startDateTime = schedule.startDateTime
        ? new Date(schedule.startDateTime)
        : new Date(`${schedule.date}T${schedule.startTime}`);
      const endDateTime = schedule.endDateTime
        ? new Date(schedule.endDateTime)
        : new Date(`${schedule.date}T${schedule.endTime}`);

      await coursesAPI.updateSchedule(courseId, scheduleId, {
        title: schedule.title,
        description: schedule.description,
        startDateTime,
        endDateTime,
        location: schedule.location,
        eventType: schedule.eventType || 'college',
        examType: schedule.examType || undefined,
        cohortId: schedule.cohortId || undefined,
        studyId: schedule.studyId || undefined,
        mode: schedule.mode || 'onsite',
        meetingUrl: schedule.meetingUrl || undefined
      });

      const updatedSchedules = await coursesAPI.getCalendarEvents();
      setSchedules(updatedSchedules || []);
      await loadData();
    } catch (error) {
      console.error('Update schedule failed', error);
      throw error;
    }
  };

  const addCalendarEvent = async (eventData) => {
    try {
      await coursesAPI.addCalendarEvent(eventData);
      const updatedSchedules = await coursesAPI.getCalendarEvents();
      setSchedules(updatedSchedules || []);
      await loadData();
    } catch (error) {
      console.error('Add calendar event failed', error);
      throw error;
    }
  };

  const updateCalendarEvent = async (eventId, eventData) => {
    try {
      await coursesAPI.updateCalendarEvent(eventId, eventData);
      const updatedSchedules = await coursesAPI.getCalendarEvents();
      setSchedules(updatedSchedules || []);
      await loadData();
    } catch (error) {
      console.error('Update calendar event failed', error);
      throw error;
    }
  };

  const deleteCalendarEvent = async (eventId) => {
    try {
      await coursesAPI.deleteCalendarEvent(eventId);
      const updatedSchedules = await coursesAPI.getCalendarEvents();
      setSchedules(updatedSchedules || []);
      await loadData();
    } catch (error) {
      console.error('Delete calendar event failed', error);
      throw error;
    }
  };

  const getStudentReport = async (studentId) => {
    return reportsAPI.getStudentProgress(studentId);
  };

  const getCourseReport = async (courseId) => {
    return reportsAPI.getCourseGrades(courseId);
  };

  const submitHomework = async (homeworkId, file, comments) => {
    return homeworkAPI.submit(homeworkId, file, comments);
  };

  const createHomework = async (data, file) => {
    return homeworkAPI.create(data, file);
  };

  const uploadHomework = async (homeworkId, file, comments) => {
    return homeworkAPI.submit(homeworkId, file, comments);
  };

  const data = {
    users,
    courses,
    enrollments,
    grades,
    homework,
    payments,
    students,
    instructors,
    studies,
    cohorts,
    departments,
    schedules,
    locations,
    currencies
  };

  const getFilteredData = (role, location) => {
    // Currently we do not have location data on studies, so fallback to all
    return { studies: data.studies || [] };
  };

  return (
    <DataContext.Provider value={{
      loading,
      courses,
      enrollments,
      grades,
      homework,
      payments,
      students,
      instructors,
      studies,
      cohorts,
      locations,
      data,
      refresh,
      getCourseById,
      getStudentEnrollments,
      getStudentGrades,
      getStudentHomeworkSubmissions,
      getStudentPayments,
      getCourseSchedule,
      getUsersByRole,
      enrollStudent,
      bulkEnrollStudents,
      bulkDeleteEnrollments,
      unenrollStudent,
      updateEnrollment,
      addUser,
      enrollStudentUser,
      updateUser,
      deleteUser,
      addCohort,
      updateCohort,
      deleteCohort,
      addStudy,
      updateStudy,
      deleteStudy,
      addStudyYear,
      updateStudyYear,
      deleteStudyYear,
      addModule,
      addCourse,
      addCourseModule,
      updateCourseModule,
      addCourseExamPoint,
      deleteCourseModule,
      deleteCourseExamPoint,
      updateCourseExamPoint,
      updateCourse,
      deleteCourse,
      addPayment,
      registerPayment,
      updatePayment,
      deletePayment,
      upsertGrade,
      addDepartment,
      updateDepartment,
      deleteDepartment,
      addCurrency,
      updateCurrency,
      deleteCurrency,
      addSchedule,
      updateSchedule,
      addCalendarEvent,
      updateCalendarEvent,
      deleteCalendarEvent,
      getStudentReport,
      getCourseReport,
      submitHomework,
      createHomework,
      uploadHomework,
      calculatePaymentSummary,
      calculateECTS,
      getFilteredData
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export default DataContext;
